import { AUTO, INITIAL, NONE } from '../constants/css-layout';
/**
 * Helpers for resolving the effective CSS `flex` of an element from its input
 * arguments (the `flex` shorthand plus the individual `flexGrow`/`flexShrink`/`flexBasis`
 * longhands), and for reasoning about whether that flex makes the element
 * non-content-sized (which is what defeats `width`/`height: fit-content` on the main axis).
 *
 * These operate on the *input* values, never on the element's computed `flex-*`
 * (which is derived from the value we set and would feed back / oscillate).
 */

// Initial values for the individual flex longhands.
const INITIAL_GROW = 0;
const INITIAL_SHRINK = 1;
const INITIAL_BASIS = AUTO;

const NUMBER_RE = /^[+-]?(\d+\.?\d*|\.\d+)$/;

const FLEX_BROWSER_DEFAULT = { grow: INITIAL_GROW, shrink: INITIAL_SHRINK, basis: INITIAL_BASIS };

const FLEX_DEFAULTS = {
  [AUTO]: { grow: 1, shrink: 1, basis: AUTO },
  [NONE]: { grow: 0, shrink: 0, basis: AUTO },
  [INITIAL]: { grow: 0, shrink: 1, basis: AUTO },
};

function constructFlexShorthand(tokens) {
  const { factors, basis = '0' } = parseFlexTokens(tokens);
  const grow = factors.length ? factors[0] : 1;
  const shrink = factors[1] ?? INITIAL_SHRINK;

  return { grow, shrink, basis };
}

/**
 * Returns true when a value was meaningfully provided (not null/undefined/empty).
 * @param {*} value
 * @returns {boolean}
 */
function isProvided(value) {
  return value !== undefined && value !== null && value !== '';
}

function parseFlexTokens(tokens) {
  const factors = [];
  let basis;

  for (const token of tokens) {
    if (NUMBER_RE.test(token)) {
      factors.push(Number(token));
    } else {
      basis = token;
    }
  }

  return { factors, basis };
}

function toToken(value) {
  return String(value).trim();
}

/**
 * Parses a CSS `flex` shorthand string into its `{ grow, shrink, basis }` components,
 * following the shorthand expansion rules:
 * - `none`    -> `0 0 auto`
 * - `auto`    -> `1 1 auto`
 * - `initial` (and empty/unset) -> `0 1 auto`
 * - one unitless number -> grow=N, shrink=1, basis=`0`
 * - one width/keyword   -> grow=1, shrink=1, basis=value
 * - `<grow> <shrink>`   -> basis=`0`
 * - `<grow> <basis>`    -> shrink=1
 * - `<grow> <shrink> <basis>`
 * Note: a unitless zero is treated as a flex factor, not a basis.
 *
 * @param {string|number} value
 * @returns {{ grow: number, shrink: number, basis: string }}
 */
export function parseFlexShorthand(value) {
  if (!isProvided(value)) return FLEX_BROWSER_DEFAULT;

  const raw = toToken(value).toLowerCase();

  if (raw in FLEX_DEFAULTS) return FLEX_DEFAULTS[raw];

  return constructFlexShorthand(raw.split(/\s+/));
}

/**
 * Resolves a single flex factor longhand override (`flexGrow`/`flexShrink`).
 * @param {*} value
 * @param {number} fallback - the value from the shorthand
 * @param {number} initial - the longhand's initial value (used for `initial`)
 * @returns {number}
 */
function resolveFactorOverride(value, fallback, initial) {
  if (!isProvided(value)) return fallback;

  const token = toToken(value).toLowerCase();
  if (token === INITIAL) return initial;

  const parsed = Number(token);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Resolves the effective flex from the input args, applying any explicit longhand
 * overrides on top of the parsed shorthand (mirroring the CSS cascade where a
 * longhand declared after the shorthand wins).
 *
 * @param {object} args
 * @param {string|number} [args.flex]
 * @param {string|number} [args.flexGrow]
 * @param {string|number} [args.flexShrink]
 * @param {string} [args.flexBasis]
 * @returns {{ grow: number, shrink: number, basis: string }}
 */
export function resolveEffectiveFlex({ flex, flexGrow, flexShrink, flexBasis } = {}) {
  const base = parseFlexShorthand(flex);

  const grow = resolveFactorOverride(flexGrow, base.grow, INITIAL_GROW);
  const shrink = resolveFactorOverride(flexShrink, base.shrink, INITIAL_SHRINK);

  let basis = base.basis;
  if (isProvided(flexBasis)) {
    const token = toToken(flexBasis).toLowerCase();
    basis = token === INITIAL ? INITIAL_BASIS : token;
  }

  return { grow, shrink, basis };
}

/**
 * Whether any of the individual flex longhand args were provided.
 * @param {object} args - same shape as resolveEffectiveFlex
 * @returns {boolean}
 */
export function hasFlexLonghandOverride({ flexGrow, flexShrink, flexBasis } = {}) {
  return isProvided(flexGrow) || isProvided(flexShrink) || isProvided(flexBasis);
}

/**
 * Whether the resolved flex makes the element non-content-sized along the main axis
 * (i.e. it grows to fill, or its basis replaces the content-derived main size).
 * The element stays content-sized only when grow is 0 AND basis is auto.
 *
 * @param {{ grow: number, basis: string }} effectiveFlex
 * @returns {boolean}
 */
export function isFlexExpanding({ grow, basis } = {}) {
  return grow > 0 || (basis !== 'auto' && basis !== INITIAL);
}

/**
 * Composes a `{ grow, shrink, basis }` object back into a `flex` shorthand string.
 * @param {{ grow: number, shrink: number, basis: string }} effectiveFlex
 * @returns {string}
 */
export function composeFlexShorthand({ grow, shrink, basis } = {}) {
  return `${grow} ${shrink} ${basis}`;
}

export const BOTH = 'both';
export const HEIGHT = 'height';
export const WIDTH = 'width';

export const AXIS_OPTIONS = Object.freeze([BOTH, HEIGHT, NONE, WIDTH]);

export default {
  composeFlexShorthand,
  hasFlexLonghandOverride,
  isFlexExpanding,
  parseFlexShorthand,
  resolveEffectiveFlex,
  BOTH,
  HEIGHT,
  NONE,
  WIDTH,
  AXIS_OPTIONS,
};
